import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPut, apiPost, apiUpload } from '../../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, UserPlus, MoreVertical, FileText, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const COLUMNS = [
  { key: 'applied', label: 'Applied', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { key: 'screening', label: 'Screening', color: 'bg-teal-50 text-teal-700 border-teal-200' },
  { key: 'interview', label: 'Interview', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'offer', label: 'Offer', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'joined', label: 'Joined', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];
const INTERVIEW_STAGES = ['interview_l1', 'interview_l2', 'interview_l3'];
const ROUND_LABEL = { interview_l1: 'Interview L1', interview_l2: 'Interview L2', interview_l3: 'Interview L3' };

const colOf = (stage) => {
  if (INTERVIEW_STAGES.includes(stage)) return 'interview';
  if (['offer', 'accepted'].includes(stage)) return 'offer';
  return stage;
};

export default function JobDetail() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [cands, setCands] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [offerFor, setOfferFor] = useState(null);
  const [menuFor, setMenuFor] = useState(null);

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const loadCands = useCallback(() => apiGet(`/hr/jobs/${jobId}/candidates`).then((r) => setCands(r.items || [])).catch(() => {}), [jobId]);
  const load = useCallback(() => {
    apiGet(`/hr/jobs/${jobId}`).then(setJob).catch(() => toast.error('Job not found'));
    loadCands();
  }, [jobId, loadCands]);
  useEffect(() => { load(); }, [load]);

  const moveStage = async (cid, stage, extra = {}) => {
    try {
      await apiPut(`/hr/candidates/${cid}/stage`, { stage, ...extra });
      toast.success('Candidate moved');
      loadCands();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Move failed'); loadCands(); }
  };

  const onDrop = async (colKey) => {
    const cid = dragId; setDragId(null); setOverCol(null);
    if (!cid) return;
    const cand = cands.find((c) => c.id === cid);
    if (!cand || colOf(cand.stage) === colKey) return;
    if (colKey === 'offer') { setOfferFor(cand); return; }
    if (colKey === 'joined') {
      if (!window.confirm(`Mark ${cand.name} as JOINED? This creates an employee record you'll complete next.`)) { loadCands(); return; }
      try {
        const r = await apiPost(`/hr/candidates/${cid}/join`, {});
        toast.success(`Employee created (${r.emp_id}) — complete their profile`);
        nav(`/app/hr/employees?edit=${r.employee_id}`);
      } catch (err) { toast.error(err?.response?.data?.detail || 'Join failed'); loadCands(); }
      return;
    }
    if (colKey === 'interview') {
      const stage = INTERVIEW_STAGES.includes(cand.stage) ? cand.stage : 'interview_l1';
      moveStage(cid, stage);
      return;
    }
    moveStage(cid, colKey);
  };

  const reject = (cand) => {
    setMenuFor(null);
    const reason = window.prompt(`Reject ${cand.name}? Enter a reason:`);
    if (reason && reason.trim()) moveStage(cand.id, 'rejected', { rejection_reason: reason.trim() });
  };
  const withdraw = (cand) => {
    setMenuFor(null);
    if (window.confirm(`Mark ${cand.name} as withdrawn?`)) moveStage(cand.id, 'withdrawn', { notes: 'Candidate withdrew' });
  };

  if (!job) return <div className="p-8 text-slate-500">Loading…</div>;
  const exited = cands.filter((c) => ['rejected', 'withdrawn'].includes(c.stage));
  const active = cands.filter((c) => !['rejected', 'withdrawn'].includes(c.stage));

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="job-detail-page" onClick={() => setMenuFor(null)}>
      <button onClick={() => nav('/app/hr/recruitment/jobs')} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[#1F7A6E]" data-testid="back-to-jobs">
        <ArrowLeft size={14} /> All Openings
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{job.title}</h1>
          <p className="text-sm text-slate-600 mt-1">{job.department || '—'}{job.grade ? ` · ${job.grade}` : ''} · {job.headcount} opening(s) · <span className="uppercase text-[11px] font-bold text-[#1F7A6E]">{job.status}</span></p>
          {job.jd_summary && <p className="text-xs text-slate-500 mt-2 max-w-2xl">{job.jd_summary}</p>}
        </div>
        <div className="flex gap-2">
          {canWrite && job.status === 'draft' && (
            <button onClick={() => apiPost(`/hr/jobs/${jobId}/approve`, {}).then(() => { toast.success('Approved'); load(); })} data-testid="approve-job-btn"
              className="px-3 py-2 border border-emerald-300 text-emerald-700 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-emerald-50">Approve & Open</button>
          )}
          {canWrite && (
            <button onClick={() => setAddOpen(true)} data-testid="add-candidate-btn"
              className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2">
              <UserPlus size={14} /> Add Candidate
            </button>
          )}
        </div>
      </div>

      {/* Kanban */}
      <div className="flex gap-3 overflow-x-auto pb-3" data-testid="candidate-kanban">
        {COLUMNS.map((col) => {
          const list = active.filter((c) => colOf(c.stage) === col.key);
          return (
            <div key={col.key}
              className={`min-w-[250px] flex-1 bg-slate-50 rounded-sm border p-2 transition-colors ${overCol === col.key ? 'border-[#1F7A6E] ring-2 ring-[#1F7A6E]/40 bg-slate-100' : 'border-slate-200'}`}
              data-testid={`col-${col.key}`}
              onDragOver={(e) => { if (dragId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overCol !== col.key) setOverCol(col.key); } }}
              onDragLeave={(e) => { if (e.currentTarget === e.target && overCol === col.key) setOverCol(null); }}
              onDrop={(e) => { e.preventDefault(); onDrop(col.key); }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <span className={`text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${col.color}`}>{col.label}</span>
                <span className="text-[10px] text-slate-500 font-display font-bold">{list.length}</span>
              </div>
              <div className="space-y-2">
                {list.map((c) => (
                  <div key={c.id} draggable={canWrite}
                    onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', c.id); }}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    className={`relative bg-white border border-slate-200 rounded-sm p-2.5 hover:shadow-sm ${canWrite ? 'cursor-grab active:cursor-grabbing' : ''} ${dragId === c.id ? 'opacity-40' : ''}`}
                    data-testid={`cand-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-1">
                      <button onClick={() => nav(`/app/hr/recruitment/candidates/${c.id}`)} className="text-left flex-1" data-testid={`cand-open-${c.id}`}>
                        <div className="font-display font-bold text-xs truncate">{c.name}</div>
                        {INTERVIEW_STAGES.includes(c.stage) && <div className="text-[9px] text-violet-600 font-bold uppercase">{ROUND_LABEL[c.stage]}</div>}
                        <div className="text-[10px] text-slate-500 truncate">{c.current_company || c.source || '—'}</div>
                        {c.expected_ctc ? <div className="text-[10px] text-slate-400 mt-0.5">Exp ₹{Number(c.expected_ctc).toLocaleString('en-IN')}</div> : null}
                        {c.offer?.offer_letter_url && <div className="text-[9px] text-amber-600 font-bold mt-1 flex items-center gap-1"><FileText size={9} /> Offer sent</div>}
                      </button>
                      {canWrite && (
                        <button onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === c.id ? null : c.id); }} data-testid={`cand-menu-${c.id}`} className="text-slate-400 hover:text-slate-700">
                          <MoreVertical size={14} />
                        </button>
                      )}
                    </div>
                    {menuFor === c.id && (
                      <div className="absolute right-2 top-7 z-20 bg-white border border-slate-200 rounded-sm shadow-lg text-xs w-32" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => reject(c)} data-testid={`reject-${c.id}`} className="block w-full text-left px-3 py-2 hover:bg-red-50 text-red-600">Reject</button>
                        <button onClick={() => withdraw(c)} data-testid={`withdraw-${c.id}`} className="block w-full text-left px-3 py-2 hover:bg-slate-50 text-slate-600">Withdraw</button>
                      </div>
                    )}
                  </div>
                ))}
                {list.length === 0 && <div className="text-[10px] text-slate-300 text-center py-4">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Rejected / Withdrawn */}
      {exited.length > 0 && (
        <details className="bg-white border border-slate-200 rounded-sm" data-testid="exited-section">
          <summary className="px-4 py-2.5 cursor-pointer text-xs font-display font-bold uppercase tracking-wider text-slate-500">
            Rejected / Withdrawn ({exited.length})
          </summary>
          <div className="px-4 pb-3 divide-y divide-slate-100">
            {exited.map((c) => (
              <button key={c.id} onClick={() => nav(`/app/hr/recruitment/candidates/${c.id}`)} className="w-full text-left py-2 flex items-center justify-between hover:bg-slate-50 px-2 -mx-2" data-testid={`exited-${c.id}`}>
                <span className="text-xs">{c.name} <span className="text-slate-400">· {c.current_company || c.source}</span></span>
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm border ${c.stage === 'rejected' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-100 text-slate-500 border-slate-300'}`}>{c.stage}</span>
              </button>
            ))}
          </div>
        </details>
      )}

      {addOpen && <AddCandidateDrawer jobId={jobId} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); loadCands(); }} />}
      {offerFor && <OfferDialog cand={offerFor} onClose={() => { setOfferFor(null); loadCands(); }} onDone={() => { setOfferFor(null); loadCands(); }} />}
    </div>
  );
}

function AddCandidateDrawer({ jobId, onClose, onAdded }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', source: 'portal', current_company: '', expected_ctc: '', notice_period_days: '' });
  const [resume, setResume] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error('Candidate name is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      if (resume) fd.append('resume', resume);
      await apiUpload(`/hr/jobs/${jobId}/candidates`, fd);
      toast.success('Candidate added'); onAdded();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-testid="add-candidate-drawer">
        <header className="px-5 py-4 border-b flex items-center justify-between bg-[#1F7A6E] text-white">
          <h2 className="font-display font-bold tracking-wide uppercase text-sm">Add Candidate</h2>
          <button onClick={onClose}><X size={20} /></button>
        </header>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-3">
          <FieldI label="Full Name *" v={f.name} on={(v) => setF({ ...f, name: v })} testid="cand-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <FieldI label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} />
            <FieldI label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} />
          </div>
          <label className="block">
            <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Source</span>
            <select className="inp mt-1" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>
              {['portal', 'referral', 'agency', 'walk-in', 'linkedin', 'naukri'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <FieldI label="Current Company" v={f.current_company} on={(v) => setF({ ...f, current_company: v })} />
          <div className="grid grid-cols-2 gap-3">
            <FieldI label="Expected CTC (₹/yr)" type="number" v={f.expected_ctc} on={(v) => setF({ ...f, expected_ctc: v })} />
            <FieldI label="Notice (days)" type="number" v={f.notice_period_days} on={(v) => setF({ ...f, notice_period_days: v })} />
          </div>
          <label className="block">
            <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Resume (PDF/DOC)</span>
            <input type="file" accept=".pdf,.doc,.docx" data-testid="cand-resume-input" onChange={(e) => setResume(e.target.files[0])} className="mt-1 text-xs block w-full" />
          </label>
          <button type="submit" disabled={saving} data-testid="cand-submit-btn" className="w-full py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50">
            {saving ? 'Adding…' : 'Add Candidate'}
          </button>
        </form>
        
      </div>
    </>
  );
}

function OfferDialog({ cand, onClose, onDone }) {
  const [ctc, setCtc] = useState(cand.expected_ctc || '');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!ctc || !date) { toast.error('CTC and joining date are required'); return; }
    setBusy(true);
    try {
      await apiPost(`/hr/candidates/${cand.id}/offer`, { ctc: Number(ctc), joining_date: date });
      toast.success('Offer letter generated'); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Offer failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="offer-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-sm w-full max-w-sm p-5 space-y-3">
        <div className="flex items-center gap-2"><FileText size={16} className="text-[#1F7A6E]" /><h3 className="font-display font-bold text-sm">Generate Offer — {cand.name}</h3></div>
        <label className="block"><span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Annual CTC (₹) *</span>
          <input type="number" data-testid="offer-ctc-input" className="inp mt-1" value={ctc} onChange={(e) => setCtc(e.target.value)} /></label>
        <label className="block"><span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Joining Date *</span>
          <input type="date" data-testid="offer-date-input" className="inp mt-1" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-sm text-xs font-display font-bold uppercase tracking-wider">Cancel</button>
          <button type="submit" disabled={busy} data-testid="offer-submit-btn" className="flex-1 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Generating…' : 'Generate'}</button>
        </div>
        
      </form>
    </div>
  );
}

const FieldI = ({ label, v, on, type = 'text', testid }) => (
  <label className="block">
    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</span>
    <input type={type} data-testid={testid} className="inp mt-1" value={v} onChange={(e) => on(e.target.value)} />
  </label>
);
